import { defineMessages, useIntl } from 'react-intl';
import { FileDownloadButton } from '../FileDownloadButton';
import { FetchResponse } from '../FileDownloadButton/FileDownloadButton';
import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import FileService, {
  GetFileResponse,
  useSubjectRestrictedOptions,
} from '../../services/FileService';
import useServiceAuth from '../../hooks/useServiceAuth';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Text } from '@lifeomic/chroma-react/components/Text';

const fileService = new FileService();

const messages = defineMessages({
  tooltipTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileDownloadAction.tooltip',
    defaultMessage: 'Download',
  },
  archiveTitle: {
    id: 'phc-web-toolkit.fileManagementControls.fileDownloadAction.archiveTitle',
    defaultMessage: 'File Archived',
  },
  archiveBody: {
    id: 'phc-web-toolkit.fileManagementControls.fileDownloadAction.archiveBody',
    defaultMessage:
      'This file is not available to download because it is currently archived. Contact LifeOmic support to learn more.',
  },
  close: {
    id: 'phc-web-toolkit.fileManagementControls.fileDownloadAction.close',
    defaultMessage: 'Close',
  },
});

interface FileDownloadActionProps {
  file?: GetFileResponse;
}

export const FileDownloadAction: React.FC<FileDownloadActionProps> = ({
  file,
}) => {
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);
  const { formatMessage } = useIntl();
  const cohortResrictedOptions = useSubjectRestrictedOptions();

  const auth = useServiceAuth();

  const {
    data: fileData,
    fetchStatus,
    refetch,
  } = useQuery<FetchResponse, unknown>(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    ['files-ui', 'file', file.id],
    async () => {
      const { data: response } = await fileService.fetchDownloadLink(auth)(
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        file?.id,
        cohortResrictedOptions,
      );
      return response;
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      // If a file is archived, don't retry the request
      retry: (failureCount, error: any) =>
        failureCount <= 3 && error?.response?.status !== 422,
      onError: (err: any) => {
        if (err.response.status === 422) {
          setModalOpen(true);
        }
      },
    },
  );

  return (
    <>
      <FileDownloadButton
        aria-label={formatMessage(messages.tooltipTitle)}
        color="inverse"
        disabled={fetchStatus === 'fetching'}
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        downloadTitle={fileData?.name}
        onClick={() => refetch()}
        tooltipTitle={formatMessage(messages.tooltipTitle)}
        withTooltip
      />
      <Modal
        title={formatMessage(messages.archiveTitle)}
        isOpen={modalOpen}
        onDismiss={() => setModalOpen(false)}
        actions={
          <>
            <Button onClick={() => setModalOpen(false)}>
              {formatMessage(messages.close)}
            </Button>
          </>
        }
      >
        <Text size="subbody">{formatMessage(messages.archiveBody)}</Text>
      </Modal>
    </>
  );
};
